import { useState, useCallback, useEffect } from "react";
import {
  Flame, Dumbbell, BarChart2, TrendingUp, Target, Activity,
  Calendar, Trash2, Scale, Plus, Trophy, Award, CheckCircle
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, ResponsiveContainer
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

function fmtD(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}
function fmtDateNL(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

function ProgressTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-xs shadow-xl">
      {payload.map((p, i) => (
        <p key={i} className="font-bold" style={{ color: p.color }}>
          {p.value} {p.name === "weight_kg" ? "kg" : p.name === "fat_pct" ? "% vet" : "kg"}
        </p>
      ))}
    </div>
  );
}

function MiniCalendar({ calendar }) {
  const trained = calendar.filter(d => d.trained).length;
  const pct = calendar.length ? Math.round((trained / calendar.length) * 100) : 0;
  const weeks = [];
  for (let i = 0; i < calendar.length; i += 7) weeks.push(calendar.slice(i, i + 7));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold">Trainingsconsistentie · 90 dagen</p>
        <p className="text-[#C9A84C] font-black">{pct}%</p>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-1">
            {week.map((day, di) => (
              <div key={di} title={day.date + (day.trained ? " ✓" : "")}
                className={`w-3 h-3 rounded-sm ${day.trained ? "bg-[#C9A84C]" : "bg-white/5"}`} />
            ))}
          </div>
        ))}
      </div>
      <p className="text-zinc-700 text-xs mt-2">{trained} / {calendar.length} dagen getraind</p>
    </div>
  );
}

function TrainingDetailModal({ log, onClose, onSaved }) {
  const [exercises, setExercises] = useState(() =>
    (log.exercises || []).map(ex => ({ ...ex, sets: (ex.sets || []).map(s => ({ ...s })) }))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const updateSet = (exIdx, setIdx, field, value) => {
    setExercises(prev => prev.map((ex, ei) =>
      ei !== exIdx ? ex : {
        ...ex,
        sets: ex.sets.map((s, si) =>
          si !== setIdx ? s : { ...s, [field]: value === "" ? "" : field === "reps" ? parseInt(value) || 0 : parseFloat(value) || 0 }
        ),
      }
    ));
  };

  const saveChanges = async () => {
    setSaving(true);
    const token = localStorage.getItem("fitsmith_token") || "";
    try {
      await fetch(`${API}/api/workout-log/${log.log_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ exercises }),
      });
      setSaved(true);
      setTimeout(() => { setSaved(false); onSaved(); }, 1200);
    } catch (e) {}
    setSaving(false);
  };

  const hasExercises = exercises.length > 0 && exercises.some(ex => ex.sets?.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}>
      <div className="w-full sm:max-w-lg bg-[#111] border border-white/10 rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-white/6 shrink-0">
          <div>
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-0.5">Training</p>
            <h3 className="text-white font-bold text-lg leading-tight">{log.naam}</h3>
            <p className="text-zinc-500 text-xs mt-0.5">
              {new Date(log.logged_at).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              {log.duration_seconds > 60 && ` · ${Math.round(log.duration_seconds / 60)} min`}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors p-1 mt-0.5">✕</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {!hasExercises ? (
            <div className="py-8 text-center">
              <Dumbbell size={28} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Geen oefeningen vastgelegd bij deze training.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {exercises.map((ex, ei) => (
                <div key={ei}>
                  <p className="text-white font-bold text-sm mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-black shrink-0"
                      style={{ background: `${GOLD}20`, color: GOLD }}>{ei + 1}</span>
                    {ex.naam}
                  </p>
                  <div className="grid grid-cols-12 gap-2 mb-1 px-1">
                    <span className="col-span-2 text-zinc-700 text-[10px] uppercase tracking-widest">Set</span>
                    <span className="col-span-5 text-zinc-700 text-[10px] uppercase tracking-widest">Gewicht (kg)</span>
                    <span className="col-span-5 text-zinc-700 text-[10px] uppercase tracking-widest">Reps</span>
                  </div>
                  <div className="space-y-1.5">
                    {(ex.sets || []).map((s, si) => (
                      <div key={si} className="grid grid-cols-12 gap-2 items-center">
                        <span className="col-span-2 text-zinc-600 text-xs font-bold text-center">{si + 1}</span>
                        <input type="number" step="0.5" min="0" value={s.kg ?? ""} onChange={e => updateSet(ei, si, "kg", e.target.value)}
                          placeholder="–" data-testid={`edit-kg-${ei}-${si}`}
                          className="col-span-5 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
                        <input type="number" min="0" value={s.reps ?? ""} onChange={e => updateSet(ei, si, "reps", e.target.value)}
                          placeholder="–" data-testid={`edit-reps-${ei}-${si}`}
                          className="col-span-5 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm text-center focus:outline-none focus:border-[#C9A84C]/50 transition-colors" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {hasExercises && (
          <div className="px-6 py-4 border-t border-white/6 shrink-0 flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-400 hover:text-white text-sm font-bold transition-colors">Sluiten</button>
            <button onClick={saveChanges} disabled={saving} data-testid="save-training-edits-btn"
              className="flex-2 px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
              style={{ background: saved ? "#22c55e" : GOLD, color: "#000", flex: 2 }}>
              {saved ? <><CheckCircle size={14} /> Opgeslagen!</> : saving ? "Opslaan..." : "Wijzigingen opslaan"}
            </button>
          </div>
        )}
        {!hasExercises && (
          <div className="px-6 py-4 border-t border-white/6 shrink-0">
            <button onClick={onClose} className="w-full py-3 rounded-xl border border-white/10 text-zinc-400 hover:text-white text-sm font-bold transition-colors">Sluiten</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProgressSection({ user }) {
  const [progressData, setProgressData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [tab, setTab] = useState("overzicht");
  const [selectedEx, setSelectedEx] = useState("");
  const [form, setForm] = useState({ weight_kg: "", fat_pct: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [detailLog, setDetailLog] = useState(null);

  const loadProgress = useCallback(async () => {
    const token = localStorage.getItem("fitsmith_token") || "";
    const h = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    try {
      const [pr, me, lg] = await Promise.all([
        fetch(`${API}/api/progress-dashboard`, { headers: h }).then(r => r.json()),
        fetch(`${API}/api/body-measurements`, { headers: h }).then(r => r.json()),
        fetch(`${API}/api/my-training-logs`, { headers: h }).then(r => r.json()),
      ]);
      setProgressData(pr);
      setMeasurements(me.measurements || []);
      setAllLogs(lg.logs || []);
      const keys = Object.keys(pr.strength_progress || {});
      if (keys.length > 0) {
        const BIG3 = ["deadlift", "squat", "benchpress", "bench press", "bankdrukken"];
        const big3Key = keys.find(k => BIG3.some(b => k.toLowerCase().includes(b)));
        setSelectedEx(big3Key || keys[0]);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const saveMeasurement = async () => {
    if (!form.weight_kg && !form.fat_pct) return;
    setSaving(true);
    const token = localStorage.getItem("fitsmith_token") || "";
    await fetch(`${API}/api/body-measurements`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
        fat_pct: form.fat_pct ? parseFloat(form.fat_pct) : null,
      }),
    });
    setForm({ weight_kg: "", fat_pct: "" });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    loadProgress();
    setSaving(false);
  };

  const handleDeleteLog = async (logId) => {
    setDeletingId(logId);
    const token = localStorage.getItem("fitsmith_token") || "";
    try {
      await fetch(`${API}/api/workout-log/${logId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      setAllLogs(prev => prev.filter(l => l.log_id !== logId));
      loadProgress();
    } catch (e) {}
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const exData = selectedEx && progressData?.strength_progress?.[selectedEx]
    ? progressData.strength_progress[selectedEx].map(p => ({ ...p, date: fmtD(p.date) }))
    : [];

  const motivatie = (progressData?.streak ?? 0) >= 7 ? "Sterker dan vorige week 🔥"
    : (progressData?.streak ?? 0) >= 3 ? "Je bent op een roll! Blijf gaan 💪"
    : (progressData?.this_week ?? 0) >= 2 ? "Lekker bezig deze week!"
    : "Consistency beats motivation";

  const TABS = [
    { id: "overzicht", label: "Consistentie", icon: <Calendar size={12} /> },
    { id: "kracht", label: "Kracht", icon: <TrendingUp size={12} /> },
    { id: "lichaam", label: "Lichaam", icon: <Scale size={12} /> },
    { id: "records", label: "Records & Badges", icon: <Trophy size={12} /> },
    { id: "trainingen", label: "Trainingen", icon: <Dumbbell size={12} />, count: allLogs.length },
  ];

  return (
    <div className="account-card mb-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-0.5">Mijn Voortgang</p>
          <h2 className="text-white font-bold text-lg leading-none">Progress Dashboard</h2>
        </div>
        {(progressData?.streak ?? 0) > 0 && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: `${GOLD}12`, border: `1px solid ${GOLD}30` }}>
            <Flame size={13} style={{ color: GOLD }} />
            <span className="text-xs font-black" style={{ color: GOLD }}>{progressData.streak}d streak</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { icon: <Flame size={16} />, label: "Streak", value: progressData?.streak ?? "–", sub: progressData?.max_streak > 0 ? `Record: ${progressData.max_streak}d` : "", gold: (progressData?.streak ?? 0) > 0 },
          { icon: <Target size={16} />, label: "Deze week", value: `${progressData?.this_week ?? 0}x`, sub: "trainingen" },
          { icon: <Dumbbell size={16} />, label: "Totaal", value: progressData?.total_sessions ?? "–", sub: "sessies" },
          { icon: <Activity size={16} />, label: "Laatste", value: progressData?.last_training ? fmtDateNL(progressData.last_training) : "–", sub: "" },
        ].map(({ icon, label, value, sub, gold }) => (
          <div key={label} className="rounded-xl p-3"
            style={{ background: gold ? `${GOLD}08` : "#0a0a0a", border: `1px solid ${gold ? `${GOLD}25` : "rgba(255,255,255,0.05)"}` }}>
            <div className="mb-1" style={{ color: gold ? GOLD : "#52525b" }}>{icon}</div>
            <p className={`font-black text-xl leading-none ${gold ? "text-[#C9A84C]" : "text-white"}`}>{value}</p>
            <p className="text-zinc-600 text-[10px] mt-1 uppercase tracking-wider font-medium">{label}</p>
            {sub && <p className="text-zinc-700 text-[10px]">{sub}</p>}
          </div>
        ))}
      </div>

      <div className="mb-4 px-4 py-2.5 rounded-xl" style={{ background: `${GOLD}06`, border: `1px solid ${GOLD}15` }}>
        <p className="text-[#C9A84C] text-xs font-semibold italic">"{motivatie}"</p>
      </div>

      <div className="flex gap-1 mb-5 bg-[#0a0a0a] border border-white/5 rounded-xl p-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`progress-tab-${t.id}`}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-all
              ${tab === t.id ? "bg-[#C9A84C] text-black" : "text-zinc-600 hover:text-white"}`}>
            {t.icon} {t.label}
            {t.count > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-black ${tab === t.id ? "bg-black/20 text-black" : "bg-white/10 text-zinc-400"}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "overzicht" && (
        <div className="space-y-4">
          {progressData?.calendar?.length > 0 ? <MiniCalendar calendar={progressData.calendar} /> : <p className="text-zinc-600 text-sm text-center py-4">Kalender laden...</p>}
          <div className="pt-4 border-t border-white/5">
            <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-3">Weekdoelen</p>
            {[2, 3, 4].map(goal => {
              const done = Math.min(progressData?.this_week ?? 0, goal);
              return (
                <div key={goal} className="mb-3 last:mb-0">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-zinc-400">{goal}x per week</span>
                    <span className={done >= goal ? "text-[#C9A84C] font-bold" : "text-zinc-600"}>{done}/{goal}{done >= goal ? " ✓" : ""}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${Math.min((done / goal) * 100, 100)}%`, background: done >= goal ? GOLD : "#3f3f46" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pt-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
              style={{ background: (progressData?.streak ?? 0) >= 3 ? `${GOLD}08` : "#0a0a0a", border: `1px solid ${(progressData?.streak ?? 0) >= 3 ? `${GOLD}20` : "rgba(255,255,255,0.05)"}` }}>
              <span className="text-3xl font-black" style={{ color: (progressData?.streak ?? 0) > 0 ? GOLD : "#3f3f46" }}>🔥 {progressData?.streak ?? 0}</span>
              <div>
                <p className="text-white text-sm font-bold">Dagen op rij getraind</p>
                <p className="text-zinc-500 text-xs">Record: {progressData?.max_streak ?? 0} dagen</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "kracht" && (() => {
        const BIG3_KEYWORDS = ["deadlift", "squat", "benchpress", "bench press", "bankdrukken"];
        const big3Index = (name) => {
          const lower = name.toLowerCase();
          const idx = BIG3_KEYWORDS.findIndex(k => lower.includes(k));
          return idx === -1 ? 999 : idx;
        };
        const sortedExKeys = Object.keys(progressData?.strength_progress || {}).sort((a, b) => {
          const ai = big3Index(a), bi = big3Index(b);
          if (ai !== bi) return ai - bi;
          return a.localeCompare(b);
        });
        return (
        <div>
          {sortedExKeys.length === 0 ? (
            <div className="py-10 text-center">
              <BarChart2 size={32} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Log trainingen met gewichten om je progressie te zien.</p>
            </div>
          ) : (
            <>
              {/* Oefening selecteren */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-zinc-400 text-xs uppercase tracking-widest font-bold">Oefening</p>
                <select value={selectedEx} onChange={e => setSelectedEx(e.target.value)} data-testid="exercise-select"
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-zinc-300 focus:outline-none max-w-[200px] text-xs">
                  {sortedExKeys.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>

              {/* 1RM kaarten */}
              {(() => {
                const entries = progressData.strength_progress[selectedEx] || [];
                const latest = entries[entries.length - 1];
                const best = [...entries].sort((a, b) => b.max_kg - a.max_kg)[0];
                const calc1rm = (kg, reps) => reps > 0 ? +(kg * (1 + reps / 30)).toFixed(1) : kg;
                const current1rm = latest ? calc1rm(latest.max_kg, latest.reps_at_max || 1) : null;
                const best1rm = best ? calc1rm(best.max_kg, best.reps_at_max || 1) : null;
                const exDataWithRm = entries.map(p => ({
                  ...p,
                  date: fmtD(p.date),
                  est_1rm: calc1rm(p.max_kg, p.reps_at_max || 1),
                }));

                return (
                  <>
                    {/* Stats rij */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[
                        { label: "Huidig max", value: `${latest?.max_kg ?? "–"} kg`, sub: latest?.reps_at_max ? `${latest.reps_at_max} reps` : "" },
                        { label: "Geschatte 1RM", value: current1rm ? `${current1rm} kg` : "–", sub: "Epley formule", gold: true },
                        { label: "Beste 1RM ooit", value: best1rm ? `${best1rm} kg` : "–", sub: best ? fmtDateNL(best.date) : "" },
                      ].map(({ label, value, sub, gold }) => (
                        <div key={label} className="rounded-xl p-3 text-center"
                          style={{ background: gold ? `${GOLD}10` : "#0a0a0a", border: `1px solid ${gold ? `${GOLD}30` : "rgba(255,255,255,0.05)"}` }}>
                          <p className={`font-black text-base leading-tight ${gold ? "text-[#C9A84C]" : "text-white"}`}>{value}</p>
                          <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-bold mt-0.5">{label}</p>
                          {sub && <p className="text-zinc-700 text-[10px] mt-0.5">{sub}</p>}
                        </div>
                      ))}
                    </div>

                    {/* Grafiek */}
                    {exDataWithRm.length === 1 ? (
                      /* Slechts 1 meting: toon als bar + melding */
                      <div className="space-y-3">
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={exDataWithRm} margin={{ top: 4, right: 4, bottom: 4, left: -25 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} unit="kg" />
                            <Tooltip content={<ProgressTooltip />} />
                            <Bar dataKey="max_kg" fill={GOLD} radius={[6, 6, 0, 0]} maxBarSize={60} name="max_kg" />
                            <Bar dataKey="est_1rm" fill={`${GOLD}50`} radius={[6, 6, 0, 0]} maxBarSize={60} name="est_1rm" />
                          </BarChart>
                        </ResponsiveContainer>
                        <p className="text-center text-zinc-600 text-xs">Log nog een sessie om je progressie grafiek te zien</p>
                      </div>
                    ) : (
                      /* 2+ metingen: area chart + 1RM lijn */
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={exDataWithRm} margin={{ top: 4, right: 4, bottom: 4, left: -25 }}>
                          <defs>
                            <linearGradient id="pgGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={GOLD} stopOpacity={0.25} />
                              <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="rmGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.12} />
                              <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} unit="kg" />
                          <Tooltip content={<ProgressTooltip />} />
                          <Area type="monotone" dataKey="max_kg" stroke={GOLD} strokeWidth={2} fill="url(#pgGrad)"
                            dot={{ fill: GOLD, r: 3 }} activeDot={{ r: 5 }} name="max_kg" />
                          <Area type="monotone" dataKey="est_1rm" stroke="#60a5fa" strokeWidth={1.5}
                            fill="url(#rmGrad)" strokeDasharray="4 2"
                            dot={{ fill: "#60a5fa", r: 2 }} activeDot={{ r: 4 }} name="est_1rm" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}

                    {/* Geschiedenis tabel */}
                    {exDataWithRm.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-wider font-bold mb-2">Gewicht geschiedenis</p>
                        <div className="space-y-1.5">
                          {[...exDataWithRm].reverse().slice(0, 8).map((e, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white/3 transition-colors">
                              <span className="text-zinc-600 text-xs w-14 shrink-0">{e.date}</span>
                              <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all"
                                  style={{ width: `${Math.min(100, (e.max_kg / (best?.max_kg || 1)) * 100)}%`, background: GOLD }} />
                              </div>
                              <span className="text-white font-bold text-xs w-14 text-right shrink-0">{e.max_kg} kg</span>
                              <span className="text-zinc-500 text-[10px] w-16 text-right shrink-0">1RM ≈ {e.est_1rm} kg</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
        );
      })()}

      {tab === "lichaam" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Gewicht (kg)</label>
              <input type="number" step="0.1" value={form.weight_kg} onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))}
                placeholder="bijv. 82.5" data-testid="weight-input"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40" />
            </div>
            <div>
              <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Vet %</label>
              <input type="number" step="0.1" value={form.fat_pct} onChange={e => setForm(f => ({ ...f, fat_pct: e.target.value }))}
                placeholder="bijv. 18.5" data-testid="fat-input"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40" />
            </div>
          </div>
          <button onClick={saveMeasurement} disabled={saving || (!form.weight_kg && !form.fat_pct)} data-testid="save-measurement-btn"
            className="w-full py-2.5 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
            style={{ background: GOLD, color: "#000" }}>
            {saved ? <><CheckCircle size={14} /> Opgeslagen!</> : saving ? "Opslaan..." : <><Plus size={14} /> Meting opslaan</>}
          </button>
          {measurements.length >= 2 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={measurements.map(m => ({ ...m, date: fmtD(m.date) }))} margin={{ top: 4, right: 4, bottom: 4, left: -25 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ProgressTooltip />} />
                <Line type="monotone" dataKey="weight_kg" stroke={GOLD} strokeWidth={2} dot={{ fill: GOLD, r: 2 }} name="weight_kg" />
                <Line type="monotone" dataKey="fat_pct" stroke="#60a5fa" strokeWidth={2} dot={{ fill: "#60a5fa", r: 2 }} name="fat_pct" strokeDasharray="4 2" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-6 text-center"><Scale size={28} className="text-zinc-700 mx-auto mb-2" /><p className="text-zinc-600 text-sm">Voeg 2+ metingen toe om je grafiek te zien.</p></div>
          )}
        </div>
      )}

      {tab === "records" && (() => {
        const BIG3_KEYWORDS = ["deadlift", "squat", "benchpress", "bench press", "bankdrukken"];
        const big3Index = (name) => {
          const lower = (name || "").toLowerCase();
          const idx = BIG3_KEYWORDS.findIndex(k => lower.includes(k));
          return idx === -1 ? 999 : idx;
        };
        const sortedPrs = [...(progressData?.prs || [])].sort((a, b) => {
          const ai = big3Index(a.exercise), bi = big3Index(b.exercise);
          if (ai !== bi) return ai - bi;
          return b.max_kg - a.max_kg;
        });
        return (
        <div className="space-y-5">
          <div>
            <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5"><Trophy size={12} style={{ color: GOLD }} /> Persoonlijke Records</p>
            {sortedPrs.length === 0 ? (
              <p className="text-zinc-600 text-sm italic">Log trainingen met gewichten voor je PR's.</p>
            ) : (
              <div className="space-y-2">
                {sortedPrs.slice(0, 8).map((pr, i) => (
                  <div key={pr.exercise} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${i === 0 ? "border-[#C9A84C]/25" : "border-white/5"}`}
                    style={{ background: i === 0 ? `${GOLD}06` : "#0a0a0a" }}>
                    <span className="text-sm w-5 text-center">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`}</span>
                    <p className="flex-1 text-zinc-300 text-sm truncate">{pr.exercise}</p>
                    <p className={`font-black text-base ${i === 0 ? "text-[#C9A84C]" : "text-white"}`}>{pr.max_kg} kg</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="pt-4 border-t border-white/5">
            <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5">
              <Award size={12} style={{ color: GOLD }} /> Achievements ·{" "}
              <span style={{ color: GOLD }}>{(progressData?.badges || []).filter(b => b.unlocked).length}/{(progressData?.badges || []).length}</span>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(progressData?.badges || []).map(b => (
                <div key={b.id} className={`rounded-xl border p-3 text-center transition-all ${b.unlocked ? "border-[#C9A84C]/25" : "border-white/5 opacity-40 grayscale"}`}
                  style={{ background: b.unlocked ? `${GOLD}08` : "#0a0a0a" }}>
                  <div className="text-2xl mb-1.5">{b.unlocked ? b.icon : "🔒"}</div>
                  <p className={`text-xs font-bold ${b.unlocked ? "text-[#C9A84C]" : "text-zinc-500"}`}>{b.naam}</p>
                  <p className="text-zinc-700 text-[10px] mt-0.5 leading-tight">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        );
      })()}

      {tab === "trainingen" && (
        <div>
          {allLogs.length === 0 ? (
            <div className="py-10 text-center"><Dumbbell size={32} className="text-zinc-700 mx-auto mb-2" /><p className="text-zinc-500 text-sm">Nog geen trainingen gelogd.</p></div>
          ) : (
            <div className="space-y-2">
              <p className="text-zinc-600 text-xs mb-3">{allLogs.length} trainingen in totaal · tik op prullenbak om te verwijderen</p>
              {allLogs.map((log, i) => (
                <div key={log.log_id} data-testid={`training-log-row-${i}`}
                  onClick={() => confirmDeleteId !== log.log_id && setDetailLog(log)}
                  className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-white/5 bg-[#0a0a0a] hover:border-[#C9A84C]/20 hover:bg-[#C9A84C]/3 transition-all cursor-pointer">
                  <span className="text-zinc-700 text-xs font-bold w-5 shrink-0 text-center">{allLogs.length - i}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-200 text-sm font-semibold truncate">{log.naam}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-zinc-600 text-xs">{new Date(log.logged_at).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
                      {log.exercise_count > 0 && <span className="text-zinc-700 text-xs">· {log.exercise_count} oefeningen</span>}
                    </div>
                  </div>
                  {confirmDeleteId === log.log_id ? (
                    <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                      <span className="text-zinc-500 text-xs">Zeker?</span>
                      <button data-testid={`confirm-delete-log-${i}`}
                        onClick={e => { e.stopPropagation(); handleDeleteLog(log.log_id); }}
                        disabled={deletingId === log.log_id}
                        className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                        style={{ background: "rgba(239,68,68,0.15)", color: "#f87171" }}>
                        {deletingId === log.log_id ? "..." : "Ja"}
                      </button>
                      <button onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}
                        className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#71717a" }}>
                        Nee
                      </button>
                    </div>
                  ) : (
                    <button data-testid={`delete-log-btn-${i}`}
                      onClick={e => { e.stopPropagation(); setConfirmDeleteId(log.log_id); }}
                      className="shrink-0 opacity-30 hover:opacity-100 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-500/10 active:bg-red-500/20 transition-all">
                      <Trash2 size={14} className="text-zinc-500 hover:text-red-400 transition-colors" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {detailLog && (
        <TrainingDetailModal log={detailLog} onClose={() => setDetailLog(null)} onSaved={() => { setDetailLog(null); loadProgress(); }} />
      )}
    </div>
  );
}
